export const cgmTestPrivacy = `

<br/>

<p>When we tested 10 different continuous glucose monitors (CGMs), accuracy and comfort were the main focus—but one question kept coming up: where does all this sensitive health data actually end up?</p>


<p>As people living with diabetes and running an open-source closed-loop system, we care about who has access to our glucose readings. These devices transmit personal data — your glucose levels 24/7 — to apps, clouds, and sometimes third parties. We dug into privacy policies and technical details to understand the data flow.</p>

<p>A breakdown:</p>

<div style="overflow-x: auto; margin: 2rem 0;">
  <table style="width: 100%; border-collapse: collapse; border: 1px solid #ddd; font-size: 0.95em;">
    <thead>
      <tr style="background-color: #f5f5f5;">
        <th style="padding: 12px; text-align: left; border: 1px solid #ddd; font-weight: 600;">CGM System</th>
        <th style="padding: 12px; text-align: left; border: 1px solid #ddd; font-weight: 600;">Company HQ</th>
        <th style="padding: 12px; text-align: left; border: 1px solid #ddd; font-weight: 600;">Cloud Provider</th>
        <th style="padding: 12px; text-align: left; border: 1px solid #ddd; font-weight: 600;">Data Storage Location</th>
        <th style="padding: 12px; text-align: left; border: 1px solid #ddd; font-weight: 600;">Notes on Cross-Border Transfers</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td style="padding: 12px; border: 1px solid #ddd;"><strong>Dexcom (G6/G7)</strong></td>
        <td style="padding: 12px; border: 1px solid #ddd;">USA</td>
        <td style="padding: 12px; border: 1px solid #ddd;">Amazon AWS</td>
        <td style="padding: 12px; border: 1px solid #ddd;">EU (primarily Edinburgh; otherwise Germany/Ireland/Netherlands)</td>
        <td style="padding: 12px; border: 1px solid #ddd;">Data stored in EU</td>
      </tr>
      <tr style="background-color: #fafafa;">
        <td style="padding: 12px; border: 1px solid #ddd;"><strong>FreeStyle Libre (FSL)</strong></td>
        <td style="padding: 12px; border: 1px solid #ddd;">USA</td>
        <td style="padding: 12px; border: 1px solid #ddd;">Amazon AWS</td>
        <td style="padding: 12px; border: 1px solid #ddd;">EU (usually Germany/Ireland/Netherlands)</td>
        <td style="padding: 12px; border: 1px solid #ddd;">Data stored in EU</td>
      </tr>
      <tr>
        <td style="padding: 12px; border: 1px solid #ddd;"><strong>Syai (Ultra/Tag)</strong></td>
        <td style="padding: 12px; border: 1px solid #ddd;">Singapore</td>
        <td style="padding: 12px; border: 1px solid #ddd;">Amazon AWS</td>
        <td style="padding: 12px; border: 1px solid #ddd;">Frankfurt, Germany</td>
        <td style="padding: 12px; border: 1px solid #ddd;">EU-only confirmed</td>
      </tr>
      <tr style="background-color: #fafafa;">
        <td style="padding: 12px; border: 1px solid #ddd;"><strong>Hippo (H1)</strong></td>
        <td style="padding: 12px; border: 1px solid #ddd;">Singapore</td>
        <td style="padding: 12px; border: 1px solid #ddd;">Amazon AWS</td>
        <td style="padding: 12px; border: 1px solid #ddd;">Frankfurt, Germany</td>
        <td style="padding: 12px; border: 1px solid #ddd;">EU-only confirmed</td>
      </tr>
      <tr>
        <td style="padding: 12px; border: 1px solid #ddd;"><strong>Caresens Air</strong></td>
        <td style="padding: 12px; border: 1px solid #ddd;">Seoul, South Korea</td>
        <td style="padding: 12px; border: 1px solid #ddd;">Not specified (servers in Germany)</td>
        <td style="padding: 12px; border: 1px solid #ddd;">Germany</td>
        <td style="padding: 12px; border: 1px solid #ddd;">EU servers</td>
      </tr>
      <tr style="background-color: #fafafa;">
        <td style="padding: 12px; border: 1px solid #ddd;"><strong>Accu-Chek SmartGuide</strong></td>
        <td style="padding: 12px; border: 1px solid #ddd;">Switzerland</td>
        <td style="padding: 12px; border: 1px solid #ddd;">Amazon AWS</td>
        <td style="padding: 12px; border: 1px solid #ddd;">EU (usually Germany/Ireland/Netherlands)</td>
        <td style="padding: 12px; border: 1px solid #ddd;">Data stored in EU</td>
      </tr>
      <tr>
        <td style="padding: 12px; border: 1px solid #ddd;"><strong>Yuwell Anytime</strong></td>
        <td style="padding: 12px; border: 1px solid #ddd;">Huzhou, Zhejiang (China)</td>
        <td style="padding: 12px; border: 1px solid #ddd;">Not specified</td>
        <td style="padding: 12px; border: 1px solid #ddd;">Not explicitly in EU</td>
        <td style="padding: 12px; border: 1px solid #ddd;">Cross-border transfers (including to China) explicitly mentioned</td>
      </tr>
    </tbody>
  </table>
</div>

<br/>

<h3 style="margin-top: 24px; margin-bottom: 16px;">What This Means in Practice</h3>

<p>If privacy and EU data residency matter most to you, <strong>Syai, Hippo, Caresens, and SmartGuide</strong> stand out with clear EU commitments. For making a choice it's good to know that in our n=1 daily use test, Smartguide was scored lower in notifications and thickness of the insertion wire, and Hippo scored low on translations and alarm sound.</p>

<p><strong>Dexcom and FreeStyle Libre</strong> also keep data in Europe, which is reassuring for users under GDPR. However their accuracy was on the lower side compared to Syai and Caresens in our n=1 test.</p>

<p><strong>Yuwell</strong> is the outlier — its policy openly discusses cross-border transfers to China without naming a cloud provider. For privacy-focused users, this raises questions.</p>

<p>All these systems work well in daily use (some exceptionally so), but knowing where your data lives helps you pick the one that aligns with your privacy comfort level.</p>

<p>Our testing showed huge progress in CGM quality overall—and transparency is part of that progress. The more we ask these questions, the better the options become.</p>

<br/>

<p style="margin-top: 1.5em; margin-bottom: 1em;">
  <a
    href="/blog/details/Insights-from-Testing-10-CGMs---How-They-Stack-Up-in-Daily-Use"
    rel="noopener"
    style="color: #1976d2; text-decoration: underline; font-weight: 500;"
  >
    Interested in the practical aspects of 10 different CGMs? Read the comparison
  </a>
</p>

<p style="margin-top: 1em; margin-bottom: 1em;">
  <a
    href="/blog/details/Insights-from-Testing-10-CGMs---A-Comparison-in-Reliability-and-Accuracy"
    rel="noopener"
    style="color: #1976d2; text-decoration: underline; font-weight: 500;"
  >
    Interested in reliability and accuracy? Read the comparison
  </a>
</p>
<br/>


<p style="font-size: 0.9em; color: #666; font-style: italic;">
Written by <a href="/blog/details/Introducing-Luk%C3%A1%C5%A1-Tr%C4%8Dka" target="_blank" rel="noopener" style="color: #1976d2; text-decoration: underline; font-weight: 500;">Lukáš Trčka</a>, reviewed by <a href="/blog/details/Introducing-Kelvin-Kramp" target="_blank" rel="noopener" style="color: #1976d2; text-decoration: underline; font-weight: 500;">Kelvin Kramp</a> (MD, PhD).
</p>

<div style="display: flex; justify-content: center; padding: 1.5rem 0;">
<img src="https://miro.medium.com/v2/resize:fit:1400/format:webp/1*0_i4kx_n4GqjBQ5vSF9E4w.png" alt="The Opensource.clinic team" style="max-width: 100%; height: auto;" />
</div>


<!-- blog-contact-footer -->
`;
